import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import { Produzione, Gusto } from '../types';
import GelatoDataGrid from '../components/DataGrid/GelatoDataGrid';
import { createColumnDefs } from './StoricoProduzione/columnDefs';
import { GridReadyEvent, GridApi } from 'ag-grid-community';
import { useToast, useConfirm } from '../components/ui';
import { AlertTriangle, Mail } from 'lucide-react';
import './StoricoProduzionePage.css';

// Funzione di utilità per garantire che i valori siano sempre numeri
const ensureNumber = (value: any): number => {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') return value;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? 0 : parsed;
};

const StoricoProduzionePage: React.FC = () => {
  const { user } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const [produzioni, setProduzioni] = useState<Produzione[]>([]);
  const [giacenze, setGiacenze] = useState<any[]>([]);
  const [smaltimenti, setSmaltimenti] = useState<any[]>([]);
  const [gusti, setGusti] = useState<Gusto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [tipoFiltro, setTipoFiltro] = useState<'produzione' | 'giacenza' | 'smaltimento' | 'tutti'>('produzione');
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  
  // Modifica produzione
  const [editingProduzione, setEditingProduzione] = useState<Produzione | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    gusti: [] as Array<{ gusto_id: number; quantita_kg: number }>,
    note: ''
  });

  // Visualizzazione dettagli produzione
  const [viewingProduzione, setViewingProduzione] = useState<Produzione | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Carica tutti i dati in parallelo
      const [produzioniData, giacenzeData, smaltimentiData, gustiData] = await Promise.all([
        apiService.getProduzione(),
        apiService.getStoricoGiacenza().catch(() => []),
        apiService.getStoricoSmaltimento().catch(() => []),
        apiService.getGusti()
      ]);
      
      // Calcola il costo totale per ogni produzione
      const produzioniConCosto = produzioniData.map((produzione: any) => ({
        ...produzione,
        tipo: 'produzione',
        costo_totale: ensureNumber(produzione.gusti?.reduce((sum: any, gusto: any) => {
          const costoGusto = ensureNumber(gusto.food_cost) * ensureNumber(gusto.quantita_kg);
          return sum + costoGusto;
        }, 0))
      }));
      
      // Formatta giacenze
      const giacenzeFormatted = giacenzeData.map((g: any) => ({
        ...g,
        tipo: 'giacenza',
        kg_totale_giornaliero: g.kg_totale
      }));
      
      // Formatta smaltimenti
      const smaltimentiFormatted = smaltimentiData.map((s: any) => ({
        ...s,
        tipo: 'smaltimento',
        kg_totale_giornaliero: s.kg_totale
      }));
      
      setProduzioni(produzioniConCosto);
      setGiacenze(giacenzeFormatted);
      setSmaltimenti(smaltimentiFormatted);
      setGusti(gustiData);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Errore nel caricamento dei dati');
      console.error('Errore:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handler per quando la griglia è pronta
  const onGridReady = useCallback((event: GridReadyEvent) => {
    setGridApi(event.api);
  }, []);

  // Handler per i click sulle celle (per gestire le azioni)
  const onCellClicked = useCallback((event: any) => {
    // Controlla se il click è su una cella delle azioni
    if (event.colDef.field === 'actions') {
      const target = event.event.target as HTMLElement;
      const produzione = event.data as Produzione;
      
      if (target.classList.contains('btn-view')) {
        handleViewProduzione(produzione);
      } else if (target.classList.contains('btn-edit')) {
        handleEditProduzione(produzione);
      } else if (target.classList.contains('btn-delete')) {
        handleDeleteProduzione(produzione);
      } else if (target.classList.contains('btn-email')) {
        handleSendEmail(produzione);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handler stabile per AG Grid; le funzioni handle* sono ricreate ad ogni render
  }, []);

  // Gestori delle azioni
  const handleViewProduzione = (produzione: Produzione) => {
    setViewingProduzione(produzione);
    setIsViewModalOpen(true);
  };

  const handleCloseViewModal = () => {
    setIsViewModalOpen(false);
    setViewingProduzione(null);
  };

  const handleEditProduzione = (produzione: Produzione) => {
    setEditingProduzione(produzione);
    setEditFormData({
      gusti: produzione.gusti?.map(g => ({
        gusto_id: g.gusto_id,
        quantita_kg: g.quantita_kg
      })) || [],
      note: produzione.note || ''
    });
    setIsEditModalOpen(true);
  };

  const handleDeleteProduzione = async (produzione: Produzione) => {
    const ok = await confirm({
      title: 'Elimina produzione',
      message: `Vuoi eliminare la produzione del ${produzione.data}?`,
      variant: 'danger',
      confirmLabel: 'Elimina'
    });
    if (!ok) {
      return;
    }

    try {
      await apiService.deleteProduzione(produzione.id);
      setProduzioni(prev => prev.filter(p => p.id !== produzione.id));
      toast.success('Produzione eliminata');
    } catch (error: any) {
      console.error('Errore eliminazione produzione:', error);
      setError(error.message || 'Errore durante l\'eliminazione');
    }
  };

  const handleSendEmail = async (produzione: Produzione) => {
    try {
      const response = await apiService.sendEmailForProduction(produzione.id);
      
      // Aggiorna lo stato email della produzione
      setProduzioni(prev => 
        prev.map(p => 
          p.id === produzione.id 
            ? { ...p, inviato_email: true }
            : p
        )
      );
      
      toast.success('Email inviata');
      console.log('Email inviata:', response);
    } catch (error: any) {
      console.error('Errore invio email:', error);
      setError(error.message || 'Errore durante l\'invio email');
    }
  };

  const handleSaveEdit = async () => {
    if (!editingProduzione) return;

    try {
      const result = await apiService.updateProduzione(editingProduzione.id, editFormData);
      
      // Calcola il costo totale dei gusti modificati
      const costoTotale = editFormData.gusti.reduce((sum, g) => {
        const gustoInfo = gusti.find(gi => gi.id === g.gusto_id);
        return sum + ensureNumber(gustoInfo?.food_cost) * ensureNumber(g.quantita_kg);
      }, 0);
      
      // Aggiorna la produzione nello stato locale
      setProduzioni(prevProduzioni => 
        prevProduzioni.map(p => 
          p.id === editingProduzione.id 
            ? {
                ...p,
                kg_totale_giornaliero: ensureNumber(result.kg_totale),
                note: editFormData.note,
                gusti: editFormData.gusti.map(g => {
                  const gustoInfo = gusti.find(gi => gi.id === g.gusto_id);
                  return {
                    gusto_id: g.gusto_id,
                    gusto_nome: gustoInfo?.nome || '',
                    gusto_categoria: gustoInfo?.categoria || '',
                    gusto_colore: gustoInfo?.colore || '',
                    quantita_kg: ensureNumber(g.quantita_kg),
                    food_cost: ensureNumber(gustoInfo?.food_cost)
                  };
                }),
                costo_totale: ensureNumber(costoTotale)
              } 
            : p
        )
      );
      
      setIsEditModalOpen(false);
      setEditingProduzione(null);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Errore nel salvare le modifiche');
    }
  };

  const handleCancelEdit = () => {
    setIsEditModalOpen(false);
    setEditingProduzione(null);
    setEditFormData({ gusti: [], note: '' });
  };

  const canViewCosts = user?.ruolo === 'admin' || user?.ruolo === 'manager';

  // Export Excel
  const handleExportExcel = useCallback(() => {
    if (!gridApi) return;
    
    gridApi.exportDataAsExcel({
      fileName: `storico-produzione-${new Date().toISOString().split('T')[0]}.xlsx`,
      sheetName: 'Storico Produzione'
    });
  }, [gridApi]);

  // Export CSV
  const handleExportCsv = useCallback(() => {
    if (!gridApi) return;
    
    gridApi.exportDataAsCsv({
      fileName: `storico-produzione-${new Date().toISOString().split('T')[0]}.csv`
    });
  }, [gridApi]);

  // Filtra dati in base alla selezione
  const datiVisualizzati = (() => {
    let dataToFilter = [];
    switch (tipoFiltro) {
      case 'produzione':
        dataToFilter = produzioni;
        break;
      case 'giacenza':
        dataToFilter = giacenze;
        break;
      case 'smaltimento':
        dataToFilter = smaltimenti;
        break;
      case 'tutti':
        dataToFilter = [...produzioni, ...giacenze, ...smaltimenti];
        break;
      default:
        dataToFilter = produzioni;
    }
    return dataToFilter
      .filter((item: any) => new Date(item.data).getFullYear() === selectedYear)
      .sort((a: any, b: any) => new Date(b.data).getTime() - new Date(a.data).getTime());
  })();

  // Trova tutti gli anni disponibili per i filtri
  const allYears = Array.from(new Set([
    ...produzioni.map(p => new Date(p.data).getFullYear()),
    ...giacenze.map(g => new Date(g.data).getFullYear()),
    ...smaltimenti.map(s => new Date(s.data).getFullYear()),
    new Date().getFullYear()
  ])).filter(y => !isNaN(y)).sort((a, b) => b - a);

  // Calcoli statistiche
  const totalCosto = ensureNumber(datiVisualizzati.filter((d: any) => d.tipo === 'produzione').reduce((sum, p) => sum + ensureNumber(p.costo_totale), 0));

  const produzioniAnno = produzioni.filter(p => new Date(p.data).getFullYear() === selectedYear);
  const totaleProduzioniAnno = produzioniAnno.length;
  const totaleKgAnno = ensureNumber(produzioniAnno.reduce((sum, p) => sum + ensureNumber(p.kg_totale_giornaliero), 0));
  const totaleProduzioniAll = produzioni.length;
  const totaleKgAll = ensureNumber(produzioni.reduce((sum, p) => sum + ensureNumber(p.kg_totale_giornaliero), 0));

  let minDate: string | null = null;
  let maxDate: string | null = null;
  produzioni.forEach(p => {
    const time = new Date(p.data).getTime();
    if (!minDate || time < new Date(minDate).getTime()) {
      minDate = p.data;
    }
    if (!maxDate || time > new Date(maxDate).getTime()) {
      maxDate = p.data;
    }
  });

  const formatDateIt = (dateLike: string | null) => {
    if (!dateLike) return '';
    const date = new Date(dateLike);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('it-IT');
  };

  const rangeLabel = minDate && maxDate
    ? `dal ${formatDateIt(minDate)} al ${formatDateIt(maxDate)}`
    : 'nessun dato';

  // Configurazione colonne
  const columnDefs = createColumnDefs(canViewCosts);

  if (loading) {
    return <div className="loading">Caricamento...</div>;
  }

  return (
    <div className="storico-produzione-page">
      <div className="page-header">
        <h1>Storico Operazioni</h1>
        <div className="stats">
          <div className="stat-card">
            <span className="stat-label">Totale Produzioni</span>
            <span className="stat-value">{totaleProduzioniAnno}</span>
            <span className="stat-sub">anno {selectedYear}</span>
            <span className="stat-sub">{rangeLabel}</span>
            <span className="stat-sub">totale {totaleProduzioniAll}</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Kg Totali</span>
            <span className="stat-value">{totaleKgAnno.toFixed(2)} kg</span>
            <span className="stat-sub">anno {selectedYear}</span>
            <span className="stat-sub">{rangeLabel}</span>
            <span className="stat-sub">totale {totaleKgAll.toFixed(2)} kg</span>
          </div>
          {canViewCosts && tipoFiltro === 'produzione' && (
            <div className="stat-card">
              <span className="stat-label">Costo Totale</span>
              <span className="stat-value">€{totalCosto.toFixed(2)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Filtro tipo operazione */}
      <div className="filter-section" style={{ padding: '1rem', background: '#f8f9fa', borderRadius: '8px', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <label style={{ fontWeight: 600, color: '#495057' }}>Mostra:</label>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              className={`filter-btn ${tipoFiltro === 'produzione' ? 'active' : ''}`}
              onClick={() => setTipoFiltro('produzione')}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                border: 'none',
                background: tipoFiltro === 'produzione' ? '#007bff' : '#fff',
                color: tipoFiltro === 'produzione' ? '#fff' : '#495057',
                fontWeight: 500,
                cursor: 'pointer',
                boxShadow: tipoFiltro === 'produzione' ? '0 2px 8px rgba(0,123,255,0.3)' : '0 1px 3px rgba(0,0,0,0.1)',
                transition: 'all 0.2s'
              }}
            >
              Produzione ({produzioni.length})
            </button>
            <button
              className={`filter-btn ${tipoFiltro === 'giacenza' ? 'active' : ''}`}
              onClick={() => setTipoFiltro('giacenza')}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                border: 'none',
                background: tipoFiltro === 'giacenza' ? '#6c5ce7' : '#fff',
                color: tipoFiltro === 'giacenza' ? '#fff' : '#495057',
                fontWeight: 500,
                cursor: 'pointer',
                boxShadow: tipoFiltro === 'giacenza' ? '0 2px 8px rgba(108,92,231,0.3)' : '0 1px 3px rgba(0,0,0,0.1)',
                transition: 'all 0.2s'
              }}
            >
              Giacenza ({giacenze.length})
            </button>
            <button
              className={`filter-btn ${tipoFiltro === 'smaltimento' ? 'active' : ''}`}
              onClick={() => setTipoFiltro('smaltimento')}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                border: 'none',
                background: tipoFiltro === 'smaltimento' ? '#d63031' : '#fff',
                color: tipoFiltro === 'smaltimento' ? '#fff' : '#495057',
                fontWeight: 500,
                cursor: 'pointer',
                boxShadow: tipoFiltro === 'smaltimento' ? '0 2px 8px rgba(214,48,49,0.3)' : '0 1px 3px rgba(0,0,0,0.1)',
                transition: 'all 0.2s'
              }}
            >
              Smaltimento ({smaltimenti.length})
            </button>
            <button
              className={`filter-btn ${tipoFiltro === 'tutti' ? 'active' : ''}`}
              onClick={() => setTipoFiltro('tutti')}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                border: 'none',
                background: tipoFiltro === 'tutti' ? '#00b894' : '#fff',
                color: tipoFiltro === 'tutti' ? '#fff' : '#495057',
                fontWeight: 500,
                cursor: 'pointer',
                boxShadow: tipoFiltro === 'tutti' ? '0 2px 8px rgba(0,184,148,0.3)' : '0 1px 3px rgba(0,0,0,0.1)',
                transition: 'all 0.2s'
              }}
            >
              Tutti ({produzioni.length + giacenze.length + smaltimenti.length})
            </button>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', marginLeft: 'auto', flexWrap: 'wrap' }}>
            <label style={{ fontWeight: 600, color: '#495057', alignSelf: 'center', marginRight: '0.5rem' }}>Anno:</label>
            {allYears.map(year => (
              <button
                key={year}
                className={`filter-btn ${selectedYear === year ? 'active' : ''}`}
                onClick={() => setSelectedYear(year)}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  border: 'none',
                  background: selectedYear === year ? '#343a40' : '#fff',
                  color: selectedYear === year ? '#fff' : '#495057',
                  fontWeight: 500,
                  cursor: 'pointer',
                  boxShadow: selectedYear === year ? '0 2px 8px rgba(52,58,64,0.3)' : '0 1px 3px rgba(0,0,0,0.1)',
                  transition: 'all 0.2s'
                }}
              >
                {year}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <span><AlertTriangle size={16} style={{ verticalAlign: 'text-bottom', marginRight: 6 }} />{error}</span>
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {/* Griglia AG Grid */}
      <GelatoDataGrid
        rowData={datiVisualizzati}
        columnDefs={columnDefs}
        loading={loading}
        pagination={true}
        paginationPageSize={20}
        onGridReady={onGridReady}
        onCellClicked={onCellClicked}
        height="700px"
        exportable={user?.ruolo === 'admin'}
        onExportExcel={handleExportExcel}
        onExportCsv={handleExportCsv}
        defaultColDef={{
          sortable: true,
          filter: true,
          resizable: true
        }}
      />

      {/* Modal Visualizzazione Dettagli Produzione */}
      {isViewModalOpen && viewingProduzione && (
        <div className="modal-overlay" onClick={handleCloseViewModal}>
          <div className="modal view-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Dettagli Produzione - {new Date(viewingProduzione.data).toLocaleDateString('it-IT')}</h3>
              <button className="close-btn" onClick={handleCloseViewModal}>×</button>
            </div>

            <div className="modal-body">
              <div className="view-section">
                <div className="view-info-grid">
                  <div className="view-info-item">
                    <strong>Punto Vendita:</strong>
                    <span>{viewingProduzione.punto_vendita_nome}</span>
                  </div>
                  <div className="view-info-item">
                    <strong>Operatore:</strong>
                    <span>{viewingProduzione.operatore_nome}</span>
                  </div>
                  <div className="view-info-item">
                    <strong>Data Produzione:</strong>
                    <span>{new Date(viewingProduzione.data).toLocaleDateString('it-IT')}</span>
                  </div>
                  <div className="view-info-item">
                    <strong>Data Inserimento:</strong>
                    <span>{new Date(viewingProduzione.created_at).toLocaleString('it-IT')}</span>
                  </div>
                  <div className="view-info-item">
                    <strong>Kg Totali:</strong>
                    <span className="highlight-value">{ensureNumber(viewingProduzione.kg_totale_giornaliero).toFixed(2)} kg</span>
                  </div>
                  {canViewCosts && (
                    <div className="view-info-item">
                      <strong>Costo Totale:</strong>
                      <span className="highlight-value">€{ensureNumber(viewingProduzione.costo_totale).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="view-info-item">
                    <strong>Email:</strong>
                    <span className={`email-badge ${viewingProduzione.inviato_email ? 'sent' : 'not-sent'}`}>
                      {viewingProduzione.inviato_email ? '✓ Inviata' : '✗ Non inviata'}
                    </span>
                  </div>
                  {viewingProduzione.temperatura_media && (
                    <div className="view-info-item">
                      <strong>Temperatura:</strong>
                      <span>{viewingProduzione.temperatura_media}°C</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="view-section">
                <h4>Gusti Prodotti ({viewingProduzione.gusti?.length || 0})</h4>
                <div className="gusti-view-grid">
                  {viewingProduzione.gusti?.map((gusto, index) => (
                    <div key={index} className="gusto-view-card">
                      <div className="gusto-header">
                        <span 
                          className="gusto-color-dot" 
                          style={{ backgroundColor: gusto.gusto_colore || '#ccc' }}
                        ></span>
                        <div className="gusto-name-info">
                          <span className="gusto-name">{gusto.gusto_nome}</span>
                          <span className="gusto-category">{gusto.gusto_categoria}</span>
                        </div>
                      </div>
                      <div className="gusto-quantity">
                        <span className="quantity-value">{ensureNumber(gusto.quantita_kg).toFixed(1)}</span>
                        <span className="quantity-unit">kg</span>
                      </div>
                      {canViewCosts && gusto.food_cost && (
                        <div className="gusto-cost">
                          <span className="cost-label">Costo:</span>
                          <span className="cost-value">€{(ensureNumber(gusto.food_cost) * ensureNumber(gusto.quantita_kg)).toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {viewingProduzione.note && (
                <div className="view-section">
                  <h4>Note</h4>
                  <div className="note-content">
                    {viewingProduzione.note}
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              {!viewingProduzione.inviato_email && (
                <button 
                  className="btn btn-success" 
                  onClick={() => {
                    handleSendEmail(viewingProduzione);
                    handleCloseViewModal();
                  }}
                >
                  <Mail size={16} style={{ verticalAlign: 'text-bottom', marginRight: 6 }} />Invia Email
                </button>
              )}
              <button className="btn btn-secondary" onClick={handleCloseViewModal}>
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Modifica Produzione - Versione Migliorata */}
      {isEditModalOpen && editingProduzione && (
        <div className="modal-overlay" onClick={handleCancelEdit}>
          <div className="modal edit-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Modifica Produzione - {new Date(editingProduzione.data).toLocaleDateString('it-IT')}</h3>
              <button className="close-btn" onClick={handleCancelEdit}>×</button>
            </div>

            <div className="modal-body">
              <div className="info-section">
                <p><strong>Punto Vendita:</strong> {editingProduzione.punto_vendita_nome}</p>
                <p><strong>Operatore:</strong> {editingProduzione.operatore_nome}</p>
                <p><strong>Data Inserimento:</strong> {new Date(editingProduzione.created_at).toLocaleDateString('it-IT')}</p>
              </div>

              <div className="form-section">
                <h4>Gusti Prodotti</h4>
                
                <div className="gusti-form">
                  {editFormData.gusti.map((gustoProdotto, index) => {
                    const gustoInfo = gusti.find(g => g.id === gustoProdotto.gusto_id);
                    return (
                      <div key={gustoProdotto.gusto_id} className="gusto-row">
                        <div className="gusto-info">
                          <span className="gusto-name">{gustoInfo?.nome}</span>
                          <span className="gusto-category">{gustoInfo?.categoria}</span>
                        </div>
                        <div className="gusto-input">
                          <input
                            type="number"
                            min="0"
                            step="0.1"
                            value={gustoProdotto.quantita_kg}
                            onChange={(e) => {
                              const newGusti = [...editFormData.gusti];
                              newGusti[index].quantita_kg = parseFloat(e.target.value) || 0;
                              setEditFormData({ ...editFormData, gusti: newGusti });
                            }}
                            className="quantita-input"
                          />
                          <span className="unit">kg</span>
                        </div>
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => {
                            const newGusti = editFormData.gusti.filter((_, i) => i !== index);
                            setEditFormData({ ...editFormData, gusti: newGusti });
                          }}
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                </div>

                <div className="add-gusto-section">
                  <h5>Aggiungi gusto</h5>
                  <select
                    onChange={(e) => {
                      const gustoId = parseInt(e.target.value);
                      if (gustoId) {
                        const availableGusto = gusti.find(g => g.id === gustoId);
                        if (availableGusto && !editFormData.gusti.some(eg => eg.gusto_id === gustoId)) {
                          setEditFormData({
                            ...editFormData,
                            gusti: [...editFormData.gusti, { gusto_id: gustoId, quantita_kg: 0 }]
                          });
                        }
                        e.target.value = '';
                      }
                    }}
                    className="gusto-select"
                  >
                    <option value="">Seleziona un gusto...</option>
                    {gusti
                      .filter(g => g.attivo && !editFormData.gusti.some(gp => gp.gusto_id === g.id))
                      .map(gusto => (
                        <option key={gusto.id} value={gusto.id}>
                          {gusto.nome} - {gusto.categoria}
                        </option>
                      ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Note</label>
                  <textarea
                    value={editFormData.note}
                    onChange={(e) => setEditFormData({ ...editFormData, note: e.target.value })}
                    className="form-control"
                    rows={3}
                    placeholder="Note sulla produzione..."
                  />
                </div>

                <div className="totals-section">
                  <p><strong>Kg Totali: {ensureNumber(editFormData.gusti.reduce((sum, g) => sum + ensureNumber(g.quantita_kg), 0)).toFixed(2)} kg</strong></p>
                  {canViewCosts && (
                    <p><strong>Costo Totale: €{ensureNumber(editFormData.gusti.reduce((sum, g) => {
                      const gustoInfo = gusti.find(gi => gi.id === g.gusto_id);
                      return sum + ensureNumber(gustoInfo?.food_cost) * ensureNumber(g.quantita_kg);
                    }, 0)).toFixed(2)}</strong></p>
                  )}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={handleCancelEdit}>
                Annulla
              </button>
              <button className="btn btn-primary" onClick={handleSaveEdit}>
                Salva Modifiche
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoricoProduzionePage;
